import { useEffect, useRef, useState } from "react";
import { Loader2, X } from "lucide-react";
import { filterNomeInput, isNomeValido } from "@/lib/nomePessoa";
import {
  buildAtendimentoIdentificacao,
  filterAtendimentoNumeroInput,
  getAtendimentoLabels,
  limiteAtendimentoNumero,
  normalizeAtendimentoNome,
  normalizeAtendimentoNumero,
  type ModoAtendimento,
} from "@/lib/atendimentoMode";
import { findComandaAbertaPorIdentificacao, lookupComandasPorIdentificacao, type ComandaAberta } from "@caixa/lib/comandasApi";

const IDENTIFICACAO_LOOKUP_DEBOUNCE_MS = 350;

export type ComandaModalConfirm = {
  nome: string;
  enviarCozinha: boolean;
  comandaId?: number;
};

type Props = {
  open: boolean;
  modoAtendimento: ModoAtendimento;
  onClose: () => void;
  onConfirm: (data: ComandaModalConfirm) => Promise<void>;
  loading?: boolean;
};

export function ComandaModal({
  open,
  modoAtendimento,
  onClose,
  onConfirm,
  loading = false,
}: Props) {
  const labels = getAtendimentoLabels(modoAtendimento);
  const [nome, setNome] = useState("");
  const [numero, setNumero] = useState("");
  const [mesa, setMesa] = useState("");
  const [candidatas, setCandidatas] = useState<ComandaAberta[]>([]);
  const [requerSelecao, setRequerSelecao] = useState(false);
  const [selectedId, setSelectedId] = useState(0);
  const [lookupError, setLookupError] = useState("");
  const [enviarCozinha, setEnviarCozinha] = useState(true);
  const [checkingIdentificacao, setCheckingIdentificacao] = useState(false);
  const [existingComanda, setExistingComanda] = useState<ComandaAberta | null>(null);
  const lookupSeqRef = useRef(0);

  useEffect(() => {
    if (open) {
      setNome("");
      setNumero("");
      setMesa("");
      setCandidatas([]);
      setRequerSelecao(false);
      setSelectedId(0);
      setLookupError("");
      setEnviarCozinha(true);
      setCheckingIdentificacao(false);
      setExistingComanda(null);
      lookupSeqRef.current += 1;
    }
  }, [open]);

  const identificacaoNormalizada = buildAtendimentoIdentificacao(modoAtendimento, nome, numero, mesa);
  const nomeNormalizado = normalizeAtendimentoNome(nome);
  const numeroNormalizado = normalizeAtendimentoNumero(numero, modoAtendimento);
  const nomeValido = !nomeNormalizado || isNomeValido(nomeNormalizado);
  const identificacaoValida = nomeValido && Boolean(nomeNormalizado || numeroNormalizado || (modoAtendimento === "ambos" && normalizeAtendimentoNumero(mesa, "mesa")));

  useEffect(() => {
    if (!open) return;

    if (!identificacaoValida) {
      lookupSeqRef.current += 1;
      setCheckingIdentificacao(false);
      setExistingComanda(null);
      return;
    }

    const seq = ++lookupSeqRef.current;
    setSelectedId(0);
    setLookupError("");
    setRequerSelecao(false);
    setCandidatas([]);
    const controller = new AbortController();
    setCheckingIdentificacao(true);

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          if (modoAtendimento === "ambos") {
            const result = await lookupComandasPorIdentificacao(identificacaoNormalizada, { signal: controller.signal });
            if (lookupSeqRef.current !== seq) return;
            setExistingComanda(result.comanda);
            setCandidatas(result.candidatas);
            setRequerSelecao(result.requer_selecao);
            return;
          }
          const found = await findComandaAbertaPorIdentificacao(identificacaoNormalizada, {
            signal: controller.signal,
          });
          if (lookupSeqRef.current !== seq) return;
          setExistingComanda(found);
        } catch (e) {
          if (e instanceof Error && e.name === "AbortError") return;
          if (lookupSeqRef.current !== seq) return;
          setExistingComanda(null);
          if (modoAtendimento === "ambos") setLookupError("Nao foi possivel verificar as comandas. Tente novamente.");
        } finally {
          if (lookupSeqRef.current === seq) {
            setCheckingIdentificacao(false);
          }
        }
      })();
    }, IDENTIFICACAO_LOOKUP_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [open, identificacaoNormalizada, identificacaoValida, modoAtendimento]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identificacaoValida || checkingIdentificacao || loading || lookupError || (requerSelecao && !selectedId)) return;
    await onConfirm({ nome: identificacaoNormalizada, enviarCozinha, ...(modoAtendimento === "ambos" ? { comandaId: selectedId || existingComanda?.id } : {}) });
  };

  if (!open) return null;

  const canSubmit = identificacaoValida && !loading && !checkingIdentificacao && !lookupError && (!requerSelecao || selectedId > 0);
  const submitLabel = existingComanda ? labels.adicionar : labels.abrir;

  return (
    <div className="caixa-overlay">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="comanda-modal-title"
        className="caixa-modal"
      >
        <div className="caixa-modal-head">
          <h2 id="comanda-modal-title" className="caixa-modal-title">
            {modoAtendimento === "ambos" ? "Comandas" : labels.singular}
          </h2>
          <button type="button" onClick={onClose} className="caixa-modal-close" aria-label="Fechar">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="caixa-field">
            <div className="flex items-center justify-between gap-3">
              <label htmlFor="comanda-nome">Nome</label>
              <span className="text-xs text-zinc-400">{nome.length}/10</span>
            </div>
            <div className="relative">
              <input
                id="comanda-nome"
                type="text"
                autoComplete="off"
                value={nome}
                maxLength={10}
                onChange={(e) => setNome(filterNomeInput(e.target.value).slice(0, 10))}
                onBlur={() => setNome((value) => normalizeAtendimentoNome(value))}
                placeholder="Ex: João"
                className="caixa-input pr-10"
                autoFocus
                disabled={loading}
              />
              {checkingIdentificacao ? (
                <Loader2
                  className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-zinc-400"
                  aria-label="Verificando identificação"
                />
              ) : null}
            </div>
          </div>
          <div className="caixa-field">
            <label htmlFor="comanda-numero">{labels.numeroLabel}</label>
            <input
              id="comanda-numero"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              value={numero}
              maxLength={limiteAtendimentoNumero(modoAtendimento)}
              onChange={(e) => setNumero(filterAtendimentoNumeroInput(e.target.value, modoAtendimento))}
              onBlur={() => setNumero((value) => normalizeAtendimentoNumero(value, modoAtendimento))}
              placeholder="Ex: 5"
              className="caixa-input"
              disabled={loading}
            />
          </div>
          {modoAtendimento === "ambos" ? <>
            <div className="caixa-field">
              <label htmlFor="comanda-mesa">Nº Mesa</label>
              <input id="comanda-mesa" className="caixa-input" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={3} value={mesa}
                onChange={(e) => setMesa(filterAtendimentoNumeroInput(e.target.value, "mesa"))}
                onBlur={() => setMesa((value) => normalizeAtendimentoNumero(value, "mesa"))} disabled={loading} />
            </div>
            {!identificacaoValida ? <p className="caixa-alert" role="status">Preencha pelo menos Nome, Nº Comanda ou Nº Mesa.</p> : null}
            {lookupError ? <p className="caixa-alert" role="alert">{lookupError}</p> : null}
            {requerSelecao ? <div className="caixa-field">
              <label htmlFor="comanda-selecao">Selecione a comanda correta</label>
              <select id="comanda-selecao" className="caixa-input" value={selectedId} onChange={(e) => setSelectedId(Number(e.target.value))} disabled={loading}>
                <option value={0}>Selecione...</option>
                {candidatas.map((c) => <option key={c.id} value={c.id}>{c.nome} (#{c.id})</option>)}
              </select>
            </div> : null}
          </> : null}
          {identificacaoNormalizada && !checkingIdentificacao && existingComanda ? (
            <p className="caixa-alert">{labels.jaExiste} Os itens serão adicionados a ela.</p>
          ) : null}
          <label className="caixa-check" htmlFor="comanda-enviar-cozinha">
            <input
              id="comanda-enviar-cozinha"
              type="checkbox"
              checked={enviarCozinha}
              onChange={(e) => setEnviarCozinha(e.target.checked)}
              disabled={loading}
            />
            Enviar para a cozinha
          </label>
          <div className="caixa-modal-footer">
            <button type="button" onClick={onClose} className="caixa-btn-outline">
              Cancelar
            </button>
            <button type="submit" disabled={!canSubmit} className="caixa-btn-primary">
              {loading ? "Salvando..." : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
